import { useQueryClient } from '@tanstack/react-query';
import { RetailerType } from 'common/constants/entities';
import {
  useGenerateAIForTCIN,
  useGenerateAIForWPIDSingleAttributeMutation,
} from 'common/hooks/api/mutations/use-attributes-mutation';
import { AttributesQueryKey } from 'common/hooks/api/queries/use-attributes-query';
import { ContentQueryKey } from 'common/hooks/api/queries/use-content-query';
import { userService } from 'common/services/user.service';
import { PDPGenerationStatus } from 'modules/common/product-details-page/constants';
import toast from 'react-hot-toast';

interface Props {
  issuesOnly?: boolean;
  pdpGen?: boolean;
  seoGen?: boolean;
  geoGen?: boolean;
  geoRefetchReviews?: boolean;
  enhanceWithSeasonality?: boolean;
  seasonalitySourceCopy?: string;
  pid: string;
  onLoadingGenerateStatus?: (value?: PDPGenerationStatus) => void;
}

export function useGeneratePDP({
  issuesOnly,
  pdpGen,
  seoGen,
  geoGen,
  geoRefetchReviews,
  enhanceWithSeasonality,
  seasonalitySourceCopy,
  pid,
  onLoadingGenerateStatus,
}: Props) {
  const queryClient = useQueryClient();

  const { mutate: mutateGenerateRetailerAI, isLoading: isRetailerAIGenerationLoading } = useGenerateAIForTCIN();
  const { mutate: mutateGenerateWalmartAI, isLoading: isWalmartAIGenerationLoading } =
    useGenerateAIForWPIDSingleAttributeMutation();

  const handleGenerateAI = () => {
    if (onLoadingGenerateStatus) {
      onLoadingGenerateStatus(null);
    }

    const handleSuccess = () => {
      toast.success('Templates generated succesfully');
      queryClient.invalidateQueries({ queryKey: [ContentQueryKey.Content, pid] });
      queryClient.invalidateQueries({ queryKey: [AttributesQueryKey.Keywords, pid] });
    };

    const commonParams = {
      optimizeAttributes: true,
      generateAttributes: false,
      overwriteOptimizedAttributes: true,
      ...(userService.ensureRetailer(RetailerType.Walmart) ? { pdpAttributesWithIssuesOnly: issuesOnly } : {}),
    };

    switch (userService.getAPIRetailer()) {
      case RetailerType.POC:
      case RetailerType.Target:
        mutateGenerateRetailerAI(
          {
            ...commonParams,
            tcin: pid,
            pdpSeoOptimization: seoGen,
            pdpGeneration: pdpGen,
            ...(userService.ensureRetailer(RetailerType.Target)
              ? {
                  pdpGeoOptimization: geoGen,
                  ...(geoGen && geoRefetchReviews ? { pdpGeoOptimizationParams: { refetchReviews: true } } : {}),
                  ...(enhanceWithSeasonality
                    ? {
                        enhanceWithSeasonality: true,
                        seasonalityEnhancementParams: {
                          sourceCopy: seasonalitySourceCopy,
                        },
                      }
                    : {}),
                }
              : {}),
          },
          { onSuccess: handleSuccess }
        );
        break;
      case RetailerType.Walmart:
      case RetailerType.Flywheel:
        mutateGenerateWalmartAI({ ...commonParams, wpid: pid }, { onSuccess: handleSuccess });
        break;
      default:
        break;
    }
  };

  return { handleGenerateAI, isLoading: isRetailerAIGenerationLoading || isWalmartAIGenerationLoading };
}
